export enum CallEventName {
  // Inbound flow
  CallReceived = 'call_received',
  CallReported = 'call_reported',
  AttemptAnswer = 'attempt_answer',
  AttemptReject = 'attempt_reject',
  AttemptCancelled = 'attempt_cancelled',
  // Outbound flow
  AttemptCall = 'attempt_call',
  AttemptCallFailed = 'attempt_call_failed',
  StatusRinging = 'status_ringing',
  // Call status
  StatusCompleted = 'status_completed',
  StatusConnected = 'status_connected',
  StatusReconnecting = 'status_reconnecting',
  StatusDisconnected = 'status_disconnected',
  // User actions
  AttemptHangup = 'attempt_hangup',
  AttemptMute = 'attempt_mute',
  AttemptEarmuf = 'attempt_earmuf',
  AttemptMediaDeviceChange = 'attempt_media_device_change',
  AttemptKeypad = 'attempt_dmtf',
  KeypadSent = 'keypad_sent',
  // Callbacks
  MuteChanged = 'mute_changed',
  EarmuffChanged = 'earmuff_changed',
  MediaDeviceChanged = 'media_device_changed',
  // Quality
  CallQualityDegraded = 'call_quality_degraded',
  // Session
  SupabaseSessionRefreshSucceed = 'supabase_session_refresh_succeed',
  SupabaseSessionRefreshSkipped = 'supabase_session_refresh_skipped',
  VonageSessionRefreshed = 'vonage_session_refreshed',
  // Errors
  VonageError = 'vonage_error',
  VonageOperationError = 'vonage_operation_error',
  // Vonage session recovery
  VonageSessionError = 'vonage_session_error',
  VonageSessionReconnectGraceStart = 'vonage_session_reconnect_grace_start',
  VonageSessionReconnectGraceRecovered = 'vonage_session_reconnect_grace_recovered',
  VonageSessionReconnectGraceExpired = 'vonage_session_reconnect_grace_expired',
  // Media
  CallMediaTimeout = 'call_media_timeout',
  CallMediaTimeoutBurst = 'call_media_timeout_burst',
  VonageMediaError = 'vonage_media_error',
  // Call health monitoring
  CallHealthAlert = 'call_health_alert',
  CallHealthDesync = 'call_health_desync',
  CallHealthDesyncOutcome = 'call_health_desync_outcome',
  ServerForceHangup = 'server_force_hangup',
  CallPhantomDetected = 'call_phantom_detected',
  // User-reported feedback
  FeedbackReported = 'feedback_reported',
  // Device call rollups
  DeviceCallFailed = 'device_call_failed',
  DeviceCallEnded = 'device_call_ended',
}

export type DeviceCallFailedStage =
  | 'call_setup'
  | 'call_ringing'
  | 'call_in_progress'
  | 'call_ended';

export type DeviceCallEndedBy = 'local' | 'remote';

export enum CallEventType {
  Call = 'call',
}

export enum CallEventLevel {
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
}

export type CallEventPayload = {
  deviceLegId?: string;
  direction?: 'inbound' | 'outbound';
  /** E.164 caller number. Inbound: remote caller. Outbound: local user line. */
  from?: string;
  /** E.164 callee number. Inbound: local user line. Outbound: remote contact. */
  to?: string;
  /**
   * Semantic error code (e.g. `session_start_recovery_failed`,
   * `media_timeout`). Strings here so callers can pass the actual failure
   * category alongside SDK numeric codes; if a numeric Vonage code is
   * available it lives in `errorMessage` as `"{code}: {message}"`.
   */
  errorCode?: string | number;
  errorMessage?: string;
  reason?: string;
  stage?: DeviceCallFailedStage;
  errorPayload?: Record<string, unknown>;
  endedBy?: DeviceCallEndedBy;
  durationMs?: number;
  [key: string]: unknown;
};

/**
 * A single event in the camelCase `POST /v1/app/events` body. Mirrors the
 * backend's `AppEventRequest` but uses camelCase keys to match the unified
 * convention (`api-allo-remote` spec, New Relic call logs); `callTraceId` is a
 * custom field the backend reads to join the event to its outbound call leg.
 */
export type AppEvent = {
  eventName: CallEventName;
  eventLevel: CallEventLevel;
  eventType: CallEventType;
  eventPayload: CallEventPayload | null;
  eventAt: string;
  callTraceId?: string;
};

/** Request body for `POST /v1/app/events`, camelCased end to end. */
export type CreateAppEventsBody = {
  sessionId: string;
  platformName: 'macos' | 'windows' | 'web';
  platformOs: string;
  appVersion: string;
  events: AppEvent[];
};
